import React, { useEffect, useState } from 'react';
import { Box, Card, Stack, Typography, TextField, Button, CircularProgress, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Paper, Grid, IconButton, Snackbar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import ApiService from '../../../../services/api.js';
import { jsPDF } from 'jspdf';
import logo from '../../../../assets/VisibeenLogo.png';

import logger from '../../../../utils/logger';
import {
  Edit as EditIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  Work as WorkIcon,
  Home as HomeIcon,
  Comment as CommentIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  VpnKey as VpnKeyIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

const Container = styled(Box)(({ theme }) => ({
  padding: '32px',
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const TitleSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '32px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
}));

const FormPaper = styled(Box)(({ theme }) => ({
  maxWidth: '960px',
  margin: '0 auto',
  padding: '32px',
  borderRadius: '20px',
  boxShadow: '0 4px 25px rgba(0,0,0,0.05)',
  border: '1px solid #eef0f3',
  backgroundColor: '#fff',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 700,
  color: '#3b82f6',
  marginBottom: '24px',
  marginTop: '32px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  '&:first-of-type': {
    marginTop: 0,
  },
  '&::after': {
    content: '""',
    flex: 1,
    height: '1px',
    background: 'linear-gradient(to right, #dbeafe, transparent)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fcfcfd',
    '&:hover fieldset': {
      borderColor: '#3b82f6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#3b82f6',
  },
}));

const SaveButton = styled(Button)(({ theme }) => ({
  padding: '12px 40px',
  fontWeight: 700,
  textTransform: 'none',
  fontSize: '16px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  borderRadius: '12px',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
  '&:hover': {
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
  },
}));

const EmployeeEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Align fields with AddEmployee.jsx
  const [form, setForm] = useState({
    full_name: '',
    employee_code: '',
    department: '',
    education: '',
    email: '',
    phone_number: '',
    alternate_phone_number: '',
    date_of_joining: '',
    employee_type: '',
    family_member_name: '',
    family_relation: '',
    family_contact: '',
    current_address_line1: '',
    current_city: '',
    current_state: '',
    current_zip_code: '',
    current_country: 'India',
    permanent_address_line1: '',
    permanent_city: '',
    permanent_state: '',
    permanent_zip_code: '',
    permanent_country: 'India',
    report_to: '',
    designation: '',
    custom_department: '',
    custom_family_relation: '',
    role_id: 4
  });
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);

  // Fetch managers list on mount
  useEffect(() => {
    const fetchManagers = async () => {
      setManagersLoading(true);
      try {
        const res = await ApiService.get('/admin/employee/get-managers');
        const data = res?.data?.data || res?.data || res || [];
        const list = Array.isArray(data) ? data : [];
        setManagers(list);
      } catch (err) {
        logger.error('Failed to fetch managers:', err);
        setManagers([]);
      } finally {
        setManagersLoading(false);
      }
    };
    fetchManagers();
  }, []);

  // Reset Password State
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showPassword, setShowPassword] = useState(false);

  // Constants - Role options for dropdown
  const ROLES = [
    { id: 1, name: 'Super Admin', loginRole: 1 },
    { id: 2, name: 'Admin', loginRole: 2 },
    { id: 'bdm_outside', name: 'BDM Sales (outside)', loginRole: 4 },
    { id: 'bdm_inside', name: 'BDM Sales (inside)', loginRole: 4 },
    { id: 'cro', name: 'CRO', loginRole: 2 },
  ];

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copied to clipboard!', severity: 'success' });
  };

  const handleOpenResetDialog = () => {
    setNewPassword('');
    setConfirmReset(false);
    setResetDialogOpen(true);
  };

  const generateNewPassword = () => {
    const firstName = form.full_name?.split(' ')[0] || 'user';
    const random6Digits = Math.floor(100000 + Math.random() * 900000);
    const password = `${firstName}@${random6Digits}#`.replace(/\s/g, '');
    setNewPassword(password);
    setConfirmReset(true);
  };

  const confirmPasswordReset = async () => {
    try {
      // Find the selected role object to get the correct loginRole
      const selectedRoleObj = ROLES.find(r => String(r.id) === String(form.role_id));
      const loginRoleId = selectedRoleObj?.loginRole || 4; // Default to Employee (4)

      await ApiService.post('/admin/employee/reset-password', {
        employee_id: id,
        new_password: newPassword,
        role_id: loginRoleId // Pass the actual login role (BDM/Telly/Sales map to 4)
      });
      setSnackbar({ open: true, message: 'Password reset successfully!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to reset password.', severity: 'error' });
      logger.error(err);
    }
  };

  const downloadCredentialsPDF = () => {
    const doc = new jsPDF();
    const headers = [["Field", "Value"]];
    const data = [
      ["Employee Name", form.full_name],
      ["Official Email", form.official_email || form.email],
      ["Username", form.official_email || form.email],
      ["New Password", newPassword],
      ["Date", new Date().toLocaleDateString()]
    ];

    // Add Logo
    try {
      doc.addImage(logo, 'PNG', 105 - 25, 15, 50, 15);
    } catch (err) { }

    doc.setFontSize(22);
    doc.setTextColor(59, 130, 246);
    doc.text("Credentials Reset", 105, 45, { align: "center" });

    // Simple formatted text instead of table for better control
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);

    let y = 70;
    doc.text(`Employee Name: ${form.full_name}`, 20, y); y += 10;
    doc.text(`Official Email: ${form.official_email || form.email}`, 20, y); y += 10;
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, y); y += 20;

    doc.setFillColor(245, 245, 245);
    doc.rect(20, y, 170, 50, 'F');
    y += 10;

    doc.setFontSize(14);
    doc.text("New Credentials:", 30, y); y += 15;

    doc.setFontSize(12);
    doc.text(`Username: ${form.official_email || form.email}`, 30, y); y += 10;
    doc.setFont("courier", "bold");
    doc.text(`Password: ${newPassword}`, 30, y);

    doc.save(`${form.full_name}_Credentials.pdf`);
  };

  useEffect(() => {
    const fetchEmployee = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await ApiService.get(`/admin/employee/get-employee/${id}`);
        const data = res?.data?.data || res?.data || res;
        const mapped = {
          full_name: [data.first_name, data.last_name].filter(Boolean).join(' ') ||
            data.full_name ||
            (data.userDetails && [data.userDetails.first_name, data.userDetails.last_name].filter(Boolean).join(' ')) ||
            (data.userDetails && data.userDetails.name) ||
            (data.user && [data.user.first_name, data.user.last_name].filter(Boolean).join(' ')) ||
            (data.user && data.user.name) ||
            data.email?.split('@')[0] || // Extract name from email as fallback
            '',
          employee_code: data.employee_code || '',
          department: typeof data.department === 'object' ? (data.department?.name || '') : (data.department || ''),
          education: data.education || '',
          email: data.email || data.personal_email || data.official_email || '',
          official_email: data.official_email || '',
          phone_number: data.phone_number || data.phone || '',
          alternate_phone_number: data.alternate_phone_number || data.alt_phone_number || data.alternate_phone || '',
          date_of_joining: data.date_of_joining || data.joining_date || data.doj || '',
          employee_type: typeof data.employee_type === 'object' ? (data.employee_type?.name || '') : (data.employee_type || ''),
          family_member_name: data.family_member_name || '',
          family_relation: data.family_relation || data.relation_with || data.relationship || '',
          family_contact: data.family_contact || data.contact_info || '',
          current_address_line1: data.current_address_line1 || data.address_line1 || data.current_address || '',
          current_city: data.current_city || '',
          current_state: data.current_state || data.state || '',
          current_zip_code: data.current_zip_code || '',
          current_country: 'India',
          permanent_address_line1: data.permanent_address_line1 || data.permanent_address || '',
          permanent_city: data.permanent_city || '',
          permanent_state: data.permanent_state || '',
          permanent_zip_code: data.permanent_zip_code || '',
          permanent_country: 'India',
          report_to: data.report_to || data.reporting_to || '',
          designation: data.designation || '',
          custom_department: '',
          custom_family_relation: '',
          role_id: data.role_id || (data.role && data.role.id) || 4,
          // Credentials for admin reference
          plain_password: data.plain_password || '',
          login_email: data.official_email || data.email || '',
        };
        // If department is custom (not in our predefined list), set to 'Other' and populate custom_department
        const predefinedDepts = ['Accounts', 'BackOffice', 'Sales', 'Tele Team', 'Development'];
        if (mapped.department && !predefinedDepts.includes(mapped.department)) {
          mapped.custom_department = mapped.department;
          mapped.department = 'Other';
        }

        // If family relation is custom, set to 'Other'
        const predefinedRelations = ['Father', 'Mother', 'Brother', 'Sister'];
        if (mapped.family_relation && !predefinedRelations.includes(mapped.family_relation)) {
          mapped.custom_family_relation = mapped.family_relation;
          mapped.family_relation = 'Other';
        }
        setForm(mapped);
      } catch (e) {
        logger.error('Failed to load employee', e);
        setError('Failed to load employee. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchEmployee();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };

      // Auto-set designation for certain roles if not already set
      if (name === 'role_id') {
        if (value === 'cro' && !prev.designation) {
          updated.designation = 'CRO';
        } else if (value === 'bdm_outside' && !prev.designation) {
          updated.designation = 'BDM Sales (outside)';
        } else if (value === 'bdm_inside' && !prev.designation) {
          updated.designation = 'BDM Sales (inside)';
        }
      }

      return updated;
    });
  };

  const handleCancel = () => {
    navigate('/admin-dashboard/employee');
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const nameParts = form.full_name.trim().split(' ');
      const first_name = nameParts[0] || '';
      const last_name = nameParts.slice(1).join(' ') || '';

      const payload = {
        first_name: first_name,
        last_name: last_name,
        employee_code: form.employee_code,
        education: form.education,
        email: form.email,
        phone_number: form.phone_number,
        alternate_phone_number: form.alternate_phone_number,
        date_of_joining: form.date_of_joining,
        employee_type: form.employee_type,
        family_member_name: form.family_member_name,
        family_relation: form.family_relation === 'Other' ? form.custom_family_relation : form.family_relation,
        family_contact: form.family_contact,
        current_address_line1: form.current_address_line1,
        current_city: form.current_city,
        current_state: form.current_state,
        current_zip_code: form.current_zip_code,
        current_country: form.current_country,
        permanent_address_line1: form.permanent_address_line1,
        permanent_city: form.permanent_city,
        permanent_state: form.permanent_state,
        permanent_zip_code: form.permanent_zip_code,
        permanent_country: form.permanent_country,
        designation: form.designation,
        department: form.department === 'Other' ? form.custom_department : form.department,
        report_to: form.report_to !== '' && form.report_to !== null ? Number(form.report_to) : undefined,
        role_id: (() => {
          // Find the selected role object to get the correct loginRole
          const selectedRoleObj = ROLES.find(r => String(r.id) === String(form.role_id));
          return selectedRoleObj?.loginRole || 4;
        })(),
      };

      // Try a conventional update endpoint. Adjust if your backend differs.
      const res = await ApiService.put(`/admin/employee/update-employee/${id}`, payload);
      logger.log('Save response:', res?.data || res);
      navigate('/admin-dashboard/employee');
    } catch (e) {
      logger.error('Failed to save employee', e);
      setError('Failed to save employee. Please review the fields and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <TitleSection>
        <TitleIcon>
          <EditIcon sx={{ fontSize: 24 }} />
        </TitleIcon>
        <Box>
          <Typography variant="h4" fontWeight={700} color="#1f2937">Edit Employee</Typography>
          <Typography variant="body2" color="text.secondary">Update team member profile information</Typography>
        </Box>
      </TitleSection>

      <FormPaper>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={10} gap={2}>
            <CircularProgress size={40} sx={{ color: '#3b82f6' }} />
            <Typography color="text.secondary" fontWeight={500}>Loading employee profile...</Typography>
          </Box>
        ) : (
          <Box>
            {error && (
              <Box sx={{ bgcolor: '#fef2f2', color: '#dc2626', p: 2, borderRadius: '12px', mb: 3, border: '1px solid #fee2e2' }}>
                {error}
              </Box>
            )}

            <SectionTitle>
              <BadgeIcon fontSize="small" /> Employee Information
            </SectionTitle>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <StyledTextField fullWidth label="Full Name" name="full_name" value={form.full_name} onChange={handleChange} placeholder="Full Name" />
              <StyledTextField fullWidth label="Employee Code" name="employee_code" value={form.employee_code} onChange={handleChange} placeholder="Employee Code" />

              <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                <SectionTitle sx={{ mt: 0, mb: 2 }}>
                  <VpnKeyIcon fontSize="small" /> Role & Credentials
                </SectionTitle>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <StyledTextField
                    select
                    label="Assign Role"
                    name="role_id"
                    value={form.role_id || 4}
                    onChange={handleChange}
                    sx={{ minWidth: 200 }}
                  >
                    {ROLES.map(role => (
                      <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                    ))}
                  </StyledTextField>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleOpenResetDialog}
                    startIcon={<VpnKeyIcon />}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}
                  >
                    Reset Password
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Click to generate a new password for this employee.
                  </Typography>
                </Box>

                {/* Credentials Display for Admin */}
                {(form.login_email || form.plain_password) && (
                  <Box sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: '#f0f9ff',
                    borderRadius: '12px',
                    border: '1px solid #bae6fd',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5
                  }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#0369a1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VpnKeyIcon fontSize="small" /> Employee Login Credentials
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {form.login_email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff', px: 2, py: 1, borderRadius: '8px', border: '1px solid #e0e7ff' }}>
                          <Typography variant="body2" color="text.secondary">Username:</Typography>
                          <Typography fontWeight={600} color="#1e40af">{form.login_email}</Typography>
                          <IconButton size="small" onClick={() => copyToClipboard(form.login_email)} sx={{ ml: 0.5 }}>
                            <ContentCopyIcon fontSize="small" sx={{ color: '#6366f1' }} />
                          </IconButton>
                        </Box>
                      )}

                      {form.plain_password ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff', px: 2, py: 1, borderRadius: '8px', border: '1px solid #e0e7ff' }}>
                          <Typography variant="body2" color="text.secondary">Password:</Typography>
                          <Typography fontWeight={600} color="#1e40af">
                            {showPassword ? form.plain_password : '••••••••'}
                          </Typography>
                          <IconButton size="small" onClick={() => setShowPassword(!showPassword)} sx={{ ml: 0.5 }}>
                            {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                          <IconButton size="small" onClick={() => copyToClipboard(form.plain_password)}>
                            <ContentCopyIcon fontSize="small" sx={{ color: '#6366f1' }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fefce8', px: 2, py: 1, borderRadius: '8px', border: '1px solid #fef08a' }}>
                          <Typography variant="body2" color="#ca8a04">
                            Password not stored. Click "Reset Password" to generate one.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>

              <StyledTextField fullWidth label="Department" name="department" value={form.department} onChange={handleChange} select>
                {['Accounts', 'BackOffice', 'Sales', 'Tele Team', 'Development', 'Other'].map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </StyledTextField>

              {form.department === 'Other' && (
                <StyledTextField fullWidth label="Specify Department" name="custom_department" value={form.custom_department} onChange={handleChange} placeholder="e.g. IT Support" />
              )}

              <StyledTextField fullWidth label="Education" name="education" value={form.education} onChange={handleChange} select>
                {['10th', '12th', 'Diploma', 'Graduate', 'Post Graduate', 'Doctorate'].map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </StyledTextField>

              <StyledTextField fullWidth label="Email Address" name="email" value={form.email} onChange={handleChange} type="email" />
              <StyledTextField fullWidth label="Contact Number" name="phone_number" value={form.phone_number} onChange={handleChange} />
              <StyledTextField fullWidth label="Designation" name="designation" value={form.designation} onChange={handleChange} placeholder="e.g. Lead Developer" />
              <StyledTextField fullWidth label="Alternative Contact" name="alternate_phone_number" value={form.alternate_phone_number} onChange={handleChange} />

              <StyledTextField fullWidth label="Date Of Joining" name="date_of_joining" type="date" value={form.date_of_joining || ''} onChange={handleChange} InputLabelProps={{ shrink: true }} />

              <StyledTextField fullWidth label="Employee Type" name="employee_type" value={form.employee_type} onChange={handleChange} select>
                {['Full Time', 'Part Time', 'Contract', 'Intern', 'Consultant'].map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </StyledTextField>
            </Box>

            <SectionTitle>
              <HomeIcon fontSize="small" /> Family & Address
            </SectionTitle>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
              <StyledTextField fullWidth label="Family Member Name" name="family_member_name" value={form.family_member_name} onChange={handleChange} />

              <StyledTextField fullWidth label="Relation" name="family_relation" value={form.family_relation} onChange={handleChange} select>
                {['Father', 'Mother', 'Brother', 'Sister', 'Other'].map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </StyledTextField>

              <StyledTextField fullWidth label="Family Phone" name="family_contact" value={form.family_contact} onChange={handleChange} />
            </Box>

            {form.family_relation === 'Other' && (
              <Box sx={{ mt: 3 }}>
                <StyledTextField fullWidth label="Specify Relation" name="custom_family_relation" value={form.custom_family_relation} onChange={handleChange} placeholder="e.g. Spouse" />
              </Box>
            )}

            <SectionTitle>
              <HomeIcon fontSize="small" /> Residential Address
            </SectionTitle>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' }, gap: 3 }}>
              <StyledTextField fullWidth label="Address Line 1" name="current_address_line1" value={form.current_address_line1} onChange={handleChange} />
              <StyledTextField fullWidth label="City" name="current_city" value={form.current_city} onChange={handleChange} />
              <StyledTextField fullWidth label="State" name="current_state" value={form.current_state} onChange={handleChange} />
              <StyledTextField fullWidth label="Postal Code" name="current_zip_code" value={form.current_zip_code} onChange={handleChange} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
              <SectionTitle sx={{ m: 0, flex: 1 }}>
                <HomeIcon fontSize="small" /> Permanent Address
              </SectionTitle>
              <Button
                size="small"
                onClick={() => setForm(prev => ({
                  ...prev,
                  permanent_address_line1: prev.current_address_line1,
                  permanent_city: prev.current_city,
                  permanent_state: prev.current_state,
                  permanent_zip_code: prev.current_zip_code,
                  permanent_country: prev.current_country
                }))}
                sx={{ color: '#3b82f6', fontWeight: 600, textTransform: 'none' }}
              >
                Same as Residential
              </Button>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' }, gap: 3 }}>
              <StyledTextField fullWidth label="Address Line 1" name="permanent_address_line1" value={form.permanent_address_line1} onChange={handleChange} />
              <StyledTextField fullWidth label="City" name="permanent_city" value={form.permanent_city} onChange={handleChange} />
              <StyledTextField fullWidth label="State" name="permanent_state" value={form.permanent_state} onChange={handleChange} />
              <StyledTextField fullWidth label="Postal Code" name="permanent_zip_code" value={form.permanent_zip_code} onChange={handleChange} />
            </Box>

            <SectionTitle>
              <CommentIcon fontSize="small" /> Other Details
            </SectionTitle>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Box>
                <StyledTextField
                  fullWidth
                  label="Reporting Manager"
                  name="report_to"
                  value={form.report_to}
                  onChange={handleChange}
                  select
                  sx={{ mb: 3 }}
                >
                  {managers.map(m => (
                    <MenuItem key={m.id} value={m.id}>{`${m.first_name || ''} ${m.last_name || ''}`.trim() || 'User'}</MenuItem>
                  ))}
                </StyledTextField>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#3b82f6', fontSize: '14px' }}>
                  <CloudUploadIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} /> SUPPORTING DOCUMENTS
                </Typography>
                <Box sx={{
                  p: 3, textAlign: 'center', border: '2px dashed #eef0f3', borderRadius: '16px',
                  cursor: 'pointer', '&:hover': { borderColor: '#3b82f6', bgcolor: alpha('#3b82f6', 0.02) }
                }}>
                  <CloudUploadIcon sx={{ fontSize: 32, color: '#3b82f6', mb: 1, opacity: 0.6 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Drag & drop file or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.secondary">JPG, PNG, PDF up to 5MB</Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 6 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ borderRadius: '12px', px: 4, py: 1.5, borderColor: '#eef0f3', color: '#64748b', textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <SaveButton
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving && <CircularProgress size={20} color="inherit" />}
              >
                {saving ? 'Updating...' : 'Save Changes'}
              </SaveButton>
            </Box>
          </Box>
        )}
      </FormPaper>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Reset Password</DialogTitle>
        <DialogContent>
          {!confirmReset ? (
            <Box sx={{ py: 2 }}>
              <Typography sx={{ mb: 2 }}>Are you sure you want to reset the password for <b>{form.full_name}</b>?</Typography>
              <Button variant="contained" color="primary" fullWidth onClick={generateNewPassword}>Generate New Password</Button>
            </Box>
          ) : (
            <Box sx={{ py: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>Password reset successfully!</Alert>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '12px' }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">NEW PASSWORD</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" fontFamily="monospace" fontWeight={700}>{newPassword}</Typography>
                  <IconButton size="small" onClick={() => copyToClipboard(newPassword)}><ContentCopyIcon fontSize="small" /></IconButton>
                </Box>
              </Paper>
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button variant="outlined" fullWidth onClick={downloadCredentialsPDF}>Download PDF</Button>
                <Button variant="contained" fullWidth onClick={() => { confirmPasswordReset(); setResetDialogOpen(false); }}>Done</Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!confirmReset && <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>}
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container >
  );
};

export default EmployeeEdit;

